import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { paymentAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  DollarSign,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  CreditCard,
  Loader2
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const TenantPayments = () => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadPayments();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadPayments = async () => {
    try {
      const response = await paymentAPI.getAll();
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await paymentAPI.getCheckoutStatus(sessionId);
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Thank you.');
          window.location.href = '/tenant/payments';
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired');
          setCheckingPayment(false);
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment is being processed. Please refresh if not updated.');
          setCheckingPayment(false);
          loadPayments();
        }
      } catch (error) {
        setCheckingPayment(false);
        loadPayments();
      }
    };

    poll();
  };

  const handlePayment = async (paymentId) => {
    setProcessingPayment(paymentId);
    try {
      const response = await paymentAPI.createCheckout(paymentId);
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to initiate payment';
      toast.error(message);
      setProcessingPayment(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID': return <CheckCircle className="w-6 h-6 text-emerald-400" />;
      case 'OVERDUE': return <AlertTriangle className="w-6 h-6 text-red-400" />;
      default: return <Clock className="w-6 h-6 text-amber-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'OVERDUE': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'PENDING': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const pendingPayments = payments.filter(p => p.status === 'PENDING' || p.status === 'OVERDUE');
  const completedPayments = payments.filter(p => p.status === 'PAID');

  if (loading) {
    return (
      <DashboardLayout type="tenant" title={t('tenant.payments')}>
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="tenant" title={t('tenant.payments')}>
      <div className="space-y-8" data-testid="tenant-payments-page">
        {checkingPayment && (
          <Card className="bg-gold/10 border-gold/30">
            <CardContent className="p-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-gold" />
              <span>Verifying your payment...</span>
            </CardContent>
          </Card>
        )}

        {/* Pending Payments */}
        <div>
          <h2 className="font-serif text-xl mb-4">Outstanding Payments</h2>
          {pendingPayments.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400 opacity-50" />
                <p className="text-muted-foreground">All payments are up to date!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingPayments.map((payment, index) => (
                <Card 
                  key={payment.id}
                  className={`border ${payment.status === 'OVERDUE' ? 'border-red-500/30 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'} card-animate`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`pending-payment-${payment.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(payment.status)}
                        <div>
                          <h3 className="font-medium">{payment.description}</h3>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Due: {payment.due_date}
                            </span>
                            <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(payment.status)}`}>
                              {payment.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-semibold">
                            ${(payment.amount + (payment.late_fee || 0)).toLocaleString()}
                          </p>
                          {payment.late_fee > 0 && (
                            <p className="text-sm text-red-400">
                              Includes ${payment.late_fee.toLocaleString()} late fee
                            </p>
                          )}
                        </div>
                        <Button
                          onClick={() => handlePayment(payment.id)}
                          disabled={processingPayment !== null}
                          className="bg-gold text-white hover:bg-gold-light"
                          data-testid={`pay-btn-${payment.id}`}
                        >
                          {processingPayment === payment.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CreditCard className="w-4 h-4 mr-2" />
                          )}
                          Pay Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Payment History */}
        <div>
          <h2 className="font-serif text-xl mb-4">Payment History</h2>
          {completedPayments.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-8 text-center">
                <DollarSign className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No payment history yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {completedPayments.map((payment, index) => (
                <Card 
                  key={payment.id}
                  className="bg-card border-border card-animate"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`completed-payment-${payment.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        <div>
                          <p className="font-medium">{payment.description}</p>
                          <p className="text-sm text-muted-foreground">
                            Paid: {payment.paid_date}
                          </p>
                        </div>
                      </div>
                      <p className="font-semibold">${payment.amount.toLocaleString()}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Payment Info */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <h3 className="font-serif text-lg mb-4">Payment Information</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>• Rent is due on the <strong className="text-foreground">1st of each month</strong></p>
              <p>• A reminder will be sent on the <strong className="text-foreground">3rd</strong> if payment is not received</p>
              <p>• A <strong className="text-amber-400">5% late fee</strong> will be applied if payment is not made by the <strong className="text-foreground">5th</strong></p>
              <p>• All payments are processed securely through Stripe</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TenantPayments;
